"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { SafeUser } from "@/app/types";
import toast from "react-hot-toast";
import axios from "axios";
import { Coffee } from "lucide-react";
import { useSession } from "next-auth/react";

interface PricingProps {
  currentUser?: SafeUser | null;
}

interface PricingPlan {
  name: string;
  displayName: string;
  price: number;
  originalPrice: number;
  articles: number;
  description: string;
  features: string[];
  highlight: boolean;
  badge?: string;
}

const pricingPlans: PricingPlan[] = [
  {
    name: "basic-monthly",
    displayName: "Basic",
    price: 5, // Updated from 9
    originalPrice: 9, // Original price for strikethrough
    articles: 20,
    description: "Perfect for startups",
    features: [
      "20 Articles/Month",
      "2 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
    ],
    highlight: false,
  },
  {
    name: "pro-monthly",
    displayName: "Pro",
    price: 10, // Updated from 19
    originalPrice: 19, // Original price for strikethrough
    articles: 40,
    description: "Great for growing sites",
    features: [
      "40 Articles/Month",
      "4 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "enterprise-monthly",
    displayName: "Enterprise",
    price: 29, // Updated from 49
    originalPrice: 49, // Original price for strikethrough
    articles: 100,
    description: "Built for high-traffic sites",
    features: [
      "100 Articles/Month",
      "6 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Best for Scale",
  },
  {
    name: "basic-yearly",
    displayName: "Basic",
    price: 50, // Updated from 90 (equivalent to 12 × €5)
    originalPrice: 60, // Original: 12 × €9
    articles: 20,
    description: "Perfect for startups",
    features: [
      "20 Articles/Month",
      "2 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
    ],
    highlight: false,
  },
  {
    name: "pro-yearly",
    displayName: "Pro",
    price: 100, // Updated from 190 (equivalent to 12 × €10)
    originalPrice: 120, // Original: 12 × €19
    articles: 40,
    description: "Great for growing sites",
    features: [
      "40 Articles/Month",
      "4 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "enterprise-yearly",
    displayName: "Enterprise",
    price: 300, // Updated from 490 (equivalent to 12 × €29)
    originalPrice: 348, // Original: 12 × €49
    articles: 100,
    description: "Built for high-traffic sites",
    features: [
      "100 Articles/Month",
      "6 Websites",
      "SEO Dashboard",
      "Automated SEO Analysis",
      "One-Click GitHub Publishing",
      "Priority Support",
    ],
    highlight: true,
    badge: "Best for Scale",
  },
];

const PricingSection: React.FC<PricingProps> = ({ currentUser }) => {
  const { data: session } = useSession();
  const router = useRouter();
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePlanSelection = async (planName: string) => {
    const planKey = `${planName.toLowerCase()}${isYearly ? "-yearly" : "-monthly"}`;
    // console.log("planKey", planKey);

    if (!session) {
      router.push(`/register?plan=${planName.toLowerCase()}&yearly=${isYearly}`);
    } else {
      await handleBecomePremium(planKey);
    }
  };

  const handleBecomePremium = async (selectedPlan: string) => {
    if (!session?.user.id) {
      toast.error("User not identified");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/api/stripe/subscribe", { plan: selectedPlan });
      if (response.data.url) {
        toast.success("Redirecting to payment page...");
        setTimeout(() => {
          window.location.href = response.data.url;
        }, 1000);
      } else {
        toast.success(response.data.message);
        router.refresh();
      }
    } catch (error) {
      toast.error("Error initiating subscription");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="pricing" className="bg-gray-100 py-10 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-black text-center mb-8">
          Pricing Plans
        </h2>
        <div className="flex justify-center mb-12">
          <div className="flex w-full sm:w-auto bg-white rounded-lg shadow-sm">
            <button
              onClick={() => setIsYearly(false)}
              className={`flex-1 px-4 sm:px-6 py-2 text-sm sm:text-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 ${!isYearly
                ? "bg-emerald-500 text-white"
                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`flex-1 px-4 sm:px-6 py-2 text-sm sm:text-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 relative ${isYearly
                ? "bg-emerald-500 text-white"
                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                }`}
            >
              Yearly
              <span className="absolute right-0 transform -translate-y-3/4 bg-yellow-400 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm z-10">
                Save 16%
              </span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {pricingPlans
            .filter((plan) =>
              isYearly ? plan.name.includes("-yearly") : plan.name.includes("-monthly")
            )
            .map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white p-6 rounded-lg border shadow-md text-center flex flex-col min-h-[400px] transition-all duration-300 ${plan.displayName === "Pro"
                  ? "border-2 border-emerald-500"
                  : plan.highlight
                    ? "border-gray-200 bg-emerald-50"
                    : "border-gray-200"
                  } ${loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"}`}
              >
                {plan.badge && (
                  <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2 z-30">
                    <span className="text-xs font-bold px-3 py-1 rounded-full shadow-sm bg-emerald-100 text-emerald-800">
                      {plan.badge}
                    </span>
                  </div>
                )}
                {isYearly && (
                  <span className="absolute top-6 right-2 bg-yellow-300 text-black text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm z-20 flex items-center">
                    <Coffee className="w-3 h-3 mr-1" />
                    Save {plan.name === "basic-yearly" && (2)}  {plan.name === "pro-yearly" && (4)}  {plan.name === "enterprise-yearly" && (10)} coffees
                  </span>
                )}
                <h3
                  className={`text-xl font-semibold text-gray-800 mb-3 pt-4 ${plan.badge ? "mt-6" : ""
                    }`}
                >
                  {plan.displayName}
                </h3>
                <div className="text-3xl font-bold text-gray-800 mb-2">
                  {isYearly && (
                    <s className="text-xl text-gray-500 mr-2">€{plan.originalPrice}</s>
                  )}
                  €{plan.price}
                  <span className="text-base font-normal text-gray-600">
                    {isYearly ? "/yr" : "/mo"}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                <ul className="text-left text-base text-gray-700 space-y-2 mb-6 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-emerald-500 mr-2">✔</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handlePlanSelection(plan.displayName)}
                  disabled={loading}
                  className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${loading
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-emerald-500 text-white hover:bg-emerald-600"
                    }`}
                >
                  {loading ? "Processing..." :  session ? "Subscribe" : "Sign Up"}
                </button>
              </div>
            ))}
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in forwards;
        }
      `}</style>
    </section>
  );
};

export default PricingSection;